import type { MarketQuote } from '@/types';

// ---------------------------------------------------------------------------
// Forex Service — Frankfurter.app (free, no API key, daily rates)
// + Synthetic economic/defense basket
// ---------------------------------------------------------------------------

// CORS 차단 → /api/forex 프록시 경유 (dev: vite 미들웨어, prod: 서버리스)
const FRANKFURTER_URL = '/api/forex';
const TIMEOUT_MS = 10_000;

interface FrankfurterResponse {
  amount: number;
  base: string;
  date: string;
  rates: Record<string, number>;
}

const FOREX_PAIRS = ['EUR', 'GBP', 'JPY', 'CNY', 'KRW', 'CHF', 'AUD', 'CAD', 'RUB', 'INR', 'TRY', 'SAR'] as const;

const FOREX_NAMES: Record<string, string> = {
  EUR: 'Euro',
  GBP: 'British Pound',
  JPY: 'Japanese Yen',
  CNY: 'Chinese Yuan',
  KRW: 'Korean Won',
  CHF: 'Swiss Franc',
  AUD: 'Australian Dollar',
  CAD: 'Canadian Dollar',
  RUB: 'Russian Ruble',
  INR: 'Indian Rupee',
  TRY: 'Turkish Lira',
  SAR: 'Saudi Riyal',
};

export async function fetchForexRates(): Promise<MarketQuote[]> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  const params = new URLSearchParams({
    from: 'USD',
    to: FOREX_PAIRS.join(','),
  });

  try {
    const res = await fetch(`${FRANKFURTER_URL}?${params}`, {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    });

    if (!res.ok) throw new Error(`Frankfurter responded with ${res.status}`);

    const data: FrankfurterResponse = await res.json();
    const now = new Date().toISOString();

    return FOREX_PAIRS
      .filter((pair) => data.rates[pair] != null)
      .map((pair): MarketQuote => {
        const rate = data.rates[pair];
        // Simulate 24h change: use date-seeded pseudorandom
        const seed = new Date(data.date).getTime() / 86400000;
        const change = (Math.sin(seed * 137.5 + FOREX_PAIRS.indexOf(pair)) * 0.8);

        return {
          symbol: `USD/${pair}`,
          name: `${FOREX_NAMES[pair] ?? pair}`,
          price: Math.round(rate * 10000) / 10000,
          change: Math.round(change * 10000) / 10000,
          changePercent: Math.round(change * 100) / 100,
          timestamp: now,
          category: 'forex',
          unit: pair,
        };
      });
  } catch (err) {
    console.error('[Forex] Frankfurter fetch failed', err);
    return getFallbackForex();
  } finally {
    clearTimeout(timer);
  }
}

function getFallbackForex(): MarketQuote[] {
  const now = new Date().toISOString();
  const basePrices: Record<string, number> = {
    EUR: 0.927, GBP: 0.791, JPY: 148.5, CNY: 7.24, KRW: 1325, CHF: 0.879,
    AUD: 1.52, CAD: 1.35, RUB: 91.5, INR: 83.1, TRY: 32.5, SAR: 3.75,
  };
  return FOREX_PAIRS.map((pair): MarketQuote => ({
    symbol: `USD/${pair}`,
    name: FOREX_NAMES[pair] ?? pair,
    price: basePrices[pair] ?? 1.0,
    change: 0,
    changePercent: 0,
    timestamp: now,
    category: 'forex',
    unit: pair,
  }));
}

// ---------------------------------------------------------------------------
// Synthetic Macro / Commodity Indicators
// Seeded by date for session consistency
// ---------------------------------------------------------------------------

function dailySeed(offset: number = 0): number {
  const d = new Date();
  return Math.sin((d.getFullYear() * 365 + d.getMonth() * 31 + d.getDate() + offset) * 2.71828);
}

export function fetchSyntheticCommodities(): MarketQuote[] {
  const now = new Date().toISOString();
  const s = (i: number) => dailySeed(i);

  return [
    { symbol: 'WTI', name: 'Crude Oil WTI', price: +(72.5 + s(1) * 4).toFixed(2), change: +(s(1) * 1.5).toFixed(2), changePercent: +(s(1) * 2).toFixed(2), timestamp: now, category: 'energy', unit: '$/bbl' },
    { symbol: 'BRENT', name: 'Brent Crude', price: +(76.2 + s(2) * 4).toFixed(2), change: +(s(2) * 1.5).toFixed(2), changePercent: +(s(2) * 2).toFixed(2), timestamp: now, category: 'energy', unit: '$/bbl' },
    { symbol: 'NG', name: 'Natural Gas', price: +(2.85 + s(3) * 0.3).toFixed(3), change: +(s(3) * 0.1).toFixed(3), changePercent: +(s(3) * 3).toFixed(2), timestamp: now, category: 'energy', unit: '$/MMBtu' },
    { symbol: 'COAL', name: 'Coal (API2)', price: +(95.0 + s(4) * 8).toFixed(2), change: +(s(4) * 2).toFixed(2), changePercent: +(s(4) * 2).toFixed(2), timestamp: now, category: 'energy', unit: '$/ton' },
    { symbol: 'URAN', name: 'Uranium (U3O8)', price: +(95.5 + s(5) * 5).toFixed(2), change: +(s(5) * 1.2).toFixed(2), changePercent: +(s(5) * 1.2).toFixed(2), timestamp: now, category: 'energy', unit: '$/lb' },
  ];
}

export function fetchSyntheticCommodityMetals(): MarketQuote[] {
  const now = new Date().toISOString();
  const s = (i: number) => dailySeed(i + 10);

  return [
    { symbol: 'SILVER', name: 'Silver', price: +(28.5 + s(1) * 2).toFixed(2), change: +(s(1) * 0.5).toFixed(2), changePercent: +(s(1) * 1.8).toFixed(2), timestamp: now, category: 'commodity', unit: '$/oz' },
    { symbol: 'COPPER', name: 'Copper', price: +(4.15 + s(2) * 0.2).toFixed(3), change: +(s(2) * 0.08).toFixed(3), changePercent: +(s(2) * 2).toFixed(2), timestamp: now, category: 'commodity', unit: '$/lb' },
    { symbol: 'LI', name: 'Lithium Carbonate', price: +(12500 + s(3) * 800).toFixed(0), change: +(s(3) * 200).toFixed(0), changePercent: +(s(3) * 1.6).toFixed(2), timestamp: now, category: 'commodity', unit: '$/ton' },
    { symbol: 'COBALT', name: 'Cobalt', price: +(28000 + s(4) * 2000).toFixed(0), change: +(s(4) * 500).toFixed(0), changePercent: +(s(4) * 1.8).toFixed(2), timestamp: now, category: 'commodity', unit: '$/ton' },
    { symbol: 'RARE-E', name: 'Rare Earth Index', price: +(155.0 + s(5) * 8).toFixed(2), change: +(s(5) * 2).toFixed(2), changePercent: +(s(5) * 1.3).toFixed(2), timestamp: now, category: 'commodity', unit: 'index' },
  ];
}

export function fetchSyntheticDefenseMarket(): MarketQuote[] {
  const now = new Date().toISOString();
  const s = (i: number) => dailySeed(i + 20);

  return [
    { symbol: 'LMT', name: 'Lockheed Martin', price: +(465.0 + s(1) * 15).toFixed(2), change: +(s(1) * 5).toFixed(2), changePercent: +(s(1) * 1.1).toFixed(2), timestamp: now, category: 'defense', unit: 'USD' },
    { symbol: 'RTX', name: 'RTX Corporation', price: +(95.0 + s(2) * 5).toFixed(2), change: +(s(2) * 1.5).toFixed(2), changePercent: +(s(2) * 1.6).toFixed(2), timestamp: now, category: 'defense', unit: 'USD' },
    { symbol: 'NOC', name: 'Northrop Grumman', price: +(460.0 + s(3) * 12).toFixed(2), change: +(s(3) * 4).toFixed(2), changePercent: +(s(3) * 0.9).toFixed(2), timestamp: now, category: 'defense', unit: 'USD' },
    { symbol: 'BA', name: 'Boeing Defense', price: +(175.0 + s(4) * 8).toFixed(2), change: +(s(4) * 3).toFixed(2), changePercent: +(s(4) * 1.7).toFixed(2), timestamp: now, category: 'defense', unit: 'USD' },
    { symbol: 'GD', name: 'General Dynamics', price: +(285.0 + s(5) * 10).toFixed(2), change: +(s(5) * 3.5).toFixed(2), changePercent: +(s(5) * 1.2).toFixed(2), timestamp: now, category: 'defense', unit: 'USD' },
    { symbol: 'KRIA', name: 'Korea Aerospace Ind.', price: +(55000 + s(6) * 2000).toFixed(0), change: +(s(6) * 800).toFixed(0), changePercent: +(s(6) * 1.5).toFixed(2), timestamp: now, category: 'defense', unit: 'KRW' },
    { symbol: 'HANWHA', name: 'Hanwha Aerospace', price: +(185000 + s(7) * 5000).toFixed(0), change: +(s(7) * 2000).toFixed(0), changePercent: +(s(7) * 1.1).toFixed(2), timestamp: now, category: 'defense', unit: 'KRW' },
  ];
}

export function fetchSyntheticFoodMarket(): MarketQuote[] {
  const now = new Date().toISOString();
  const s = (i: number) => dailySeed(i + 30);

  return [
    { symbol: 'WHEAT', name: 'Wheat (CBOT)', price: +(5.80 + s(1) * 0.4).toFixed(3), change: +(s(1) * 0.12).toFixed(3), changePercent: +(s(1) * 2.1).toFixed(2), timestamp: now, category: 'food', unit: '$/bu' },
    { symbol: 'CORN', name: 'Corn (CBOT)', price: +(4.55 + s(2) * 0.3).toFixed(3), change: +(s(2) * 0.08).toFixed(3), changePercent: +(s(2) * 1.8).toFixed(2), timestamp: now, category: 'food', unit: '$/bu' },
    { symbol: 'SOY', name: 'Soybeans (CBOT)', price: +(11.5 + s(3) * 0.8).toFixed(2), change: +(s(3) * 0.2).toFixed(2), changePercent: +(s(3) * 1.7).toFixed(2), timestamp: now, category: 'food', unit: '$/bu' },
    { symbol: 'RICE', name: 'Rice (CME)', price: +(17.5 + s(4) * 1.0).toFixed(2), change: +(s(4) * 0.3).toFixed(2), changePercent: +(s(4) * 1.7).toFixed(2), timestamp: now, category: 'food', unit: '$/cwt' },
    { symbol: 'BDI', name: 'Baltic Dry Index', price: +(1450 + s(5) * 200).toFixed(0), change: +(s(5) * 50).toFixed(0), changePercent: +(s(5) * 3.4).toFixed(2), timestamp: now, category: 'food', unit: 'index' },
  ];
}

export function fetchSyntheticMacro(): MarketQuote[] {
  const now = new Date().toISOString();
  const s = (i: number) => dailySeed(i + 40);

  return [
    { symbol: 'VIX', name: 'CBOE VIX', price: +(16.5 + s(1) * 3).toFixed(2), change: +(s(1) * 0.8).toFixed(2), changePercent: +(s(1) * 5).toFixed(2), timestamp: now, category: 'macro', unit: 'index' },
    { symbol: 'DXY', name: 'US Dollar Index', price: +(104.5 + s(2) * 1.5).toFixed(2), change: +(s(2) * 0.3).toFixed(2), changePercent: +(s(2) * 0.3).toFixed(2), timestamp: now, category: 'macro', unit: 'index' },
    { symbol: 'SPX', name: 'S&P 500', price: +(5850 + s(3) * 80).toFixed(2), change: +(s(3) * 25).toFixed(2), changePercent: +(s(3) * 0.4).toFixed(2), timestamp: now, category: 'macro', unit: 'index' },
    { symbol: 'NDX', name: 'NASDAQ 100', price: +(20500 + s(4) * 300).toFixed(2), change: +(s(4) * 100).toFixed(2), changePercent: +(s(4) * 0.5).toFixed(2), timestamp: now, category: 'macro', unit: 'index' },
    { symbol: 'GPR', name: 'Geopolitical Risk Index', price: +(165 + s(5) * 20).toFixed(0), change: +(s(5) * 8).toFixed(0), changePercent: +(s(5) * 4.8).toFixed(2), timestamp: now, category: 'macro', unit: 'index' },
    { symbol: 'EMBIG', name: 'EM Bond Spread', price: +(385 + s(6) * 20).toFixed(0), change: +(s(6) * 5).toFixed(0), changePercent: +(s(6) * 1.3).toFixed(2), timestamp: now, category: 'macro', unit: 'bps' },
  ];
}
